
import { Injectable, inject } from "@angular/core";
import { BaseService, RequestOptions } from "src/app/services/base.service";
import { Observable } from "rxjs";
import { UtilsService } from 'src/app/services/utils.service';
import { Task as CoreTaskDto } from "src/app/pages/task/task.service";
import { map, shareReplay } from 'rxjs/operators';

@Injectable({
	providedIn: "root",
})
export class ProjectService {

	private static readonly REQUEST_TOP_PATH: string = "task";

	private _baseService: BaseService = inject(BaseService);

	private taskdata$: Observable<any>;

	public getTasks(requestOptions?: RequestOptions): Observable<CoreTaskDto[]> {
		if (!this.taskdata$) {
		const requestEndpoint: string = "GetCoreTasks";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		this.taskdata$ = 
		this._baseService.get(
			url,
			requestOptions
		).pipe(
			map(
				(response: CoreTaskDto[]) => {
					for (let a = 0; a < response.length; a++) {
						response[a].startDate = new Date(response[a].startDate);
						response[a].deadlineDate = new Date(response[a].deadlineDate);
						response[a].createdDate = new Date(response[a].createdDate);
					}

					

					return response;
				}
			)
			,
			shareReplay(1)  
		);
		}
		return this.taskdata$;
	}

	public getDependencies(requestOptions?: RequestOptions): Observable<ProjectTaskDependency[]> {
		const requestEndpoint: string = "GetGeneralProjectTaskDependencies";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	public getResources(requestOptions?: RequestOptions): Observable<ProjectResource[]> {
		const requestEndpoint: string = "GetGeneralProjectResources";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	public getResourceAssignments(requestOptions?: RequestOptions): Observable<ProjectResourceAssignment[]> {
		const requestEndpoint: string = "GetGeneralProjectResourceAssignments";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	public getProject(projectId: number, requestOptions?: RequestOptions): Observable<Project>
	{
		const requestEndpoint: string = "GetGeneralProject";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}?projectId=${projectId}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	private projectData$: Observable<any>;

	public getProjects(requestOptions?: RequestOptions): Observable<Project[]>
	{
		if (!this.projectData$) {
		const requestEndpoint: string = "GetGeneralProjects";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		this.projectData$=this._baseService.get(
			url,
			requestOptions
		).pipe(shareReplay(1));
		}
		return this.projectData$;
	}

	public getProjectToTaskRelations(requestOptions?: RequestOptions): Observable<ProjectToProjectTask[]> {
		const requestEndpoint: string = "GetProjectToTaskRelations";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	public deleteProjectTasks(project: Project, requestOptions?: RequestOptions): Observable<StatusMessageIdResultDto> {
		const requestEndpoint: string = "DeleteProjectTasks";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.post(
			url,
			project,
			requestOptions
		);
	}

	public deleteProject(project: Project, deleteTasks: boolean, requestOptions?: RequestOptions): Observable<StatusMessageIdResultDto> {
		const requestEndpoint: string = "DeleteProject";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}?deleteTasks=${deleteTasks}`;

		return this._baseService.post(
			url,
			project,
			requestOptions
		);
	}

	public deleteDependency(dependency: Dependency, requestOptions?: RequestOptions): Observable<StatusMessageResultDto> {
		const requestEndpoint: string = "DeleteDependency";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.post(
			url,
			this.convertDependencyToProjectTaskDependency(dependency),
			requestOptions
		);
	}

	public insertDependency(dependency: Dependency, requestOptions?: RequestOptions): Observable<StatusMessageResultDto> {
		const requestEndpoint: string = "InsertDependency";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.post(
			url,
			this.convertDependencyToProjectTaskDependency(dependency),
			requestOptions
		);
	}

	public unassignResource(resourceAssignment: ResourceAssignment, requestOptions?: RequestOptions): Observable<StatusMessageResultDto> {
		const requestEndpoint: string = "UnassignResource";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.post(
			url,
			this.convertAssignmentToProjectAssignment(resourceAssignment),
			requestOptions
		);
	}

	public assignResource(resourceAssignment: ResourceAssignment, requestOptions?: RequestOptions): Observable<StatusMessageIdResultDto> {
		const requestEndpoint: string = "AssignResource";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.post(
			url,
			this.convertAssignmentToProjectAssignment(resourceAssignment),
			requestOptions
		);
	}

	public deleteResource(resource: Resource, requestOptions?: RequestOptions): Observable<StatusMessageResultDto> {
		const requestEndpoint: string = "DeleteResource";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.post(
			url,
			this.convertResourceToProjectResource(resource),
			requestOptions
		);
	}

	public insertResource(resource: Resource, requestOptions?: RequestOptions): Observable<StatusMessageIdResultDto> {
		const requestEndpoint: string = "InsertResource";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.post(
			url,
			this.convertResourceToProjectResource(resource),
			requestOptions
		);
	}

	// Grid

	public saveProjectGridData(data: any, requestOptions?: RequestOptions): Observable<StatusMessageResultDto> {
		const requestEndpoint: string = "SaveProjectGridData";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.post(
			url,
			data,
			requestOptions
		);
	}

	// <Project Templates Requests>

	//? Template Project

	public getTemplateProject(id: number, requestOptions?: RequestOptions): Observable<TemplateProject> {
		const requestEndpoint: string = "GetTemplateProject";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}?id=${id}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	public getTemplateProjects(requestOptions?: RequestOptions): Observable<TemplateProject[]> {
		const requestEndpoint: string = "GetTemplateProjects";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	public createTemplateProject(name: string, requestOptions?: RequestOptions): Observable<StatusMessageIdResultDto> {
		const requestEndpoint: string = "CreateTemplateProject";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}?name=${name}`;

		return this._baseService.post(
			url,
			undefined,
			requestOptions
		);
	}

	public cloneTemplateProject(name: string, id: number, requestOptions?: RequestOptions): Observable<StatusMessageIdResultDto> {
		const requestEndpoint: string = "CloneTemplateProject";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}?name=${name}&id=${id}`;

		return this._baseService.post(
			url,
			undefined,
			requestOptions
		);
	}

	public updateTemplateProject(templateProject: TemplateProject, requestOptions?: RequestOptions): Observable<StatusMessageIdResultDto> {
		const requestEndpoint: string = "UpdateTemplateProject";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.post(
			url,
			templateProject,
			requestOptions
		);
	}

	public deleteTemplateProject(templateProject: TemplateProject, requestOptions?: RequestOptions): Observable<StatusMessageIdResultDto> {
		const requestEndpoint: string = "DeleteTemplateProject";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.post(
			url,
			templateProject,
			requestOptions
		);
	}

	public createProjectFromTemplate(templateProjectId: number, name: string, startDate: Date, responsibleUser: string, requestOptions?: RequestOptions): Observable<StatusMessageIdResultDto> {
		const requestEndpoint: string = "CreateProjectFromTemplate";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}?templateProjectId=${templateProjectId}&name=${encodeURI(name)}&startDate=${startDate}&responsibleUser=${responsibleUser}`;

		return this._baseService.post(
			url,
			undefined,
			requestOptions
		);
	}

	//? Template Project Task

	public getTemplateProjectTasks(templateProjectId: number, requestOptions?: RequestOptions): Observable<TemplateProjectTask[]> {
		const requestEndpoint: string = "GetTemplateProjectTasks";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}?templateProjectId=${templateProjectId}`;

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	public deleteTemplateProjectTask(templateProjectTask: TemplateProjectTask, requestOptions?: RequestOptions): Observable<StatusMessageResultDto> {
		const requestEndpoint: string = "DeleteTemplateProjectTask";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.post(
			url,
			templateProjectTask,
			requestOptions
		);
	}

	public createTemplateProjectTask(templateProjectTask: TemplateProjectTask, requestOptions?: RequestOptions): Observable<StatusMessageIdResultDto> {
		const requestEndpoint: string = "CreateTemplateProjectTask";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.post(
			url,
			templateProjectTask,
			requestOptions
		);
	}

	public updateTemplateProjectTask(templateProjectTask: TemplateProjectTask, requestOptions?: RequestOptions): Observable<StatusMessageIdResultDto> {
		const requestEndpoint: string = "UpdateTemplateProjectTask";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.post(
			url,
			templateProjectTask,
			requestOptions
		);
	}

	//~ Dependencies.

	// templateProjectId: number
	public getTemplateProjectTaskDependencies(requestOptions?: RequestOptions): Observable<ProjectTaskDependency[]> {
		const requestEndpoint: string = "GetTemplateProjectTaskDependencies";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;// `?templateProjectId=${templateProjectId}`

		return this._baseService.get(
			url,
			requestOptions
		);
	}

	public deleteTemplateProjectTaskDependency(dependency: Dependency, requestOptions?: RequestOptions): Observable<StatusMessageResultDto> {
		const requestEndpoint: string = "DeleteTemplateProjectTaskDependency";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.post(
			url,
			this.convertDependencyToProjectTaskDependency(dependency),
			requestOptions
		);
	}

	public insertTemplateProjectTaskDependency(dependency: Dependency, requestOptions?: RequestOptions): Observable<StatusMessageResultDto> {
		const requestEndpoint: string = "InsertTemplateProjectTaskDependency";

		let url = `${ProjectService.REQUEST_TOP_PATH}/${requestEndpoint}`;

		return this._baseService.post(
			url,
			this.convertDependencyToProjectTaskDependency(dependency),
			requestOptions
		);
	}

	// <Project Templates Requests>

	

	public convertProjectTaskDependencyToDependency(projectTaskDependency: ProjectTaskDependency): Dependency {
		let dependency: Dependency = new Dependency();

		dependency.id = projectTaskDependency.id;
		dependency.predecessorId = projectTaskDependency.predecessorTaskId;
		dependency.successorId = projectTaskDependency.successorTaskId;
		dependency.type = 0;

		return dependency;
	}

	public convertProjectTaskDependenciesToDependencies(projectTaskDependencies: ProjectTaskDependency[]): Dependency[] {
		let result: Dependency[] = [];

		projectTaskDependencies.forEach(
			projectTaskDependency => result.push(this.convertProjectTaskDependencyToDependency(projectTaskDependency))
		);

		return result;
	}

	public convertDependencyToProjectTaskDependency(dependency: Dependency): ProjectTaskDependency {
		let projectTaskDependency: ProjectTaskDependency = new ProjectTaskDependency();

		projectTaskDependency.id = dependency.id;
		projectTaskDependency.predecessorTaskId = dependency.predecessorId;
		projectTaskDependency.successorTaskId = dependency.successorId;

		return projectTaskDependency;
	}

	public convertDependenciesToProjectTaskDependencies(dependencies: Dependency[]): ProjectTaskDependency[] {
		let result: ProjectTaskDependency[] = [];

		dependencies.forEach(
			dependency => result.push(this.convertDependencyToProjectTaskDependency(dependency))
		);

		return result;
	}

	public convertProjectResourceToResource(projectResource: ProjectResource): Resource {
		let resource: Resource = new Resource();

		resource.id = projectResource.id;
		resource.text = projectResource.text;

		return resource;
	}

	public convertProjectResourcesToResources(projectResources: ProjectResource[]): Resource[] {
		let result: Resource[] = [];

		projectResources.forEach(
			projectResource => result.push(this.convertProjectResourceToResource(projectResource))
		);

		return result;
	}

	public convertResourceToProjectResource(resource: Resource): ProjectResource {
		let projectResource: ProjectResource = new ProjectResource();

		projectResource.id = resource.id;
		projectResource.text = resource.text;

		return projectResource;
	}

	public convertResourcesToProjectResources(resources: Resource[]): ProjectResource[] {
		let result: ProjectResource[] = [];

		resources.forEach(
			resource => result.push(this.convertResourceToProjectResource(resource))
		);

		return result;
	}

	public convertProjectAssignmentToAssignment(projectResourceAssignment: ProjectResourceAssignment): ResourceAssignment {
		let resourceAssignment = new ResourceAssignment();

		resourceAssignment.id = projectResourceAssignment.id;
		resourceAssignment.resourceId = projectResourceAssignment.projectResourceId;
		resourceAssignment.taskId = projectResourceAssignment.projectTaskId;
		resourceAssignment.userId = projectResourceAssignment.userId;

		return resourceAssignment;
	}

	public convertProjectAssignmentsToAssignments(projectResourceAssignments: ProjectResourceAssignment[]): ResourceAssignment[] {
		let result: ResourceAssignment[] = [];

		projectResourceAssignments.forEach(
			projectResourceAssignment => result.push(this.convertProjectAssignmentToAssignment(projectResourceAssignment))
		);

		return result;
	}

	public convertAssignmentToProjectAssignment(resourceAssignment: ResourceAssignment): ProjectResourceAssignment {
		let projectResourceAssignment = new ProjectResourceAssignment();

		projectResourceAssignment.id = resourceAssignment.id;
		projectResourceAssignment.projectResourceId = resourceAssignment.resourceId;
		projectResourceAssignment.projectTaskId = resourceAssignment.taskId;
		projectResourceAssignment.userId = resourceAssignment.userId;

		return projectResourceAssignment;
	}

	public convertAssignmentsToProjectAssignments(resourceAssignments: ResourceAssignment[]): ProjectResourceAssignment[] {
		let result: ProjectResourceAssignment[] = [];

		resourceAssignments.forEach(
			resourceAssignment => result.push(this.convertAssignmentToProjectAssignment(resourceAssignment))
		);

		return result;
	}
}

export class StatusMessageResultDto {
	success: boolean;
	message: string;
}

export class StatusMessageIdResultDto {
	success: boolean;
	message: string;
	id: number;
}

// DevExtreme classes
// export class Task

export class Dependency
{
	id: number;
	predecessorId: number;
	successorId: number;
	type: number;
}

export class Resource
{
	id: number;
	text: string;

	newId: number = 0;

	/**
	 *
	 */
	constructor(id: number = -1, text: string = "") {
		this.id = id;
		this.text = text;
	}
}

export class ResourceAssignment
{
	id: number;
	taskId: number;
	resourceId: number;
	userId: number;
}

export class Project
{
	id: number;
	name: string;
	responsibleUser: string;
	isActive: boolean;
	createdOn: Date | number | string;
	// main: boolean;
}

export class ProjectProgressGroup
{
	ProjectId: number;
	ProgressGroup: number;
	Count: number;
}

export class ProjectProgressMatrixUnit {
	ProjectId: number;
	Count: number;
	Status: string;
	PlannedStatus: string;
}

export class ProjectTaskStatus
{
	Id: number;
	Progress: number;
	Status: string;
	Count: number;
}



export class ProjectTaskDependency
{
	id: number;
	predecessorTaskId: number;
	successorTaskId: number;
}

export class ProjectResource
{
	id: number;
	text: string;
}

export class ProjectResourceAssignment
{
	id: number;
	projectTaskId: number;
	projectResourceId: number;
	userId: number;
}

export class ProjectToProjectTask
{
	id: number;
	projectTaskId: number;
	projectId: number;
}


// <Project Templates Specific>

export class TemplateProject {
	Id: number;
	Name: string;
	MaxDueDate: Date | number | string;
	CanHaveNestedTasks: boolean;
	CanHaveTaskResources: boolean;
	CanHaveTaskSuccessors: boolean;
	TemplateProjectPriotyId: number;
	StartDate: Date | number | string;
}

export class TemplateProjectTask {
	id: number;
	parentTemplateTaskId: number;
	templateProjectId: number;
	templateTaskName: string;
	templateTaskDescription: string;
	responsibleUser: string;
	responsibleUserId: number;
	taskStartDaysFromProjectStartDate: number;
	taskDeadlineDaysFromTaskStart: number;
	projectTaskOrder: number;
	priority: string;
	allowReminder: boolean;
	reminderDays: number;
	link: string;
	solutionDescription: string;
	taskGroup1: string;
	taskGroup2: string;
	taskGroup3: string;
	taskGroup4: string;
	taskGroup5: string;
	isActive: boolean;

	progress: number;
	startDate: Date | number | string;
	deadlineDate: Date | number | string;

	newIdToReplaceAutogeneratedGUID: number = 0;

	public initialize(templateProject: TemplateProject): void {
		this.startDate = UtilsService.addDaysToDate(templateProject.StartDate, this.taskStartDaysFromProjectStartDate);
		this.deadlineDate = UtilsService.addDaysToDate(this.startDate, this.taskDeadlineDaysFromTaskStart);

		this.progress = 0;
	}

	private calculateDates(templateProject: TemplateProject): void {
		let startDifferenceInDays = UtilsService.dateDifferenceInDays(new Date(templateProject.StartDate), this.startDate);

		this.taskStartDaysFromProjectStartDate = startDifferenceInDays > 0 ? startDifferenceInDays : 0;

		let deadlineDifferenceInDays = UtilsService.dateDifferenceInDays(this.startDate, this.deadlineDate);

		this.taskDeadlineDaysFromTaskStart = deadlineDifferenceInDays > 0 ? deadlineDifferenceInDays : 0;
	}

	public prepareForCreation(templateProject: TemplateProject): void {
		console.log(`prepareForCreation | Task: ${this.templateTaskName}, Id: ${this.id}`);

		this.id = -1;

		this.calculateDates(templateProject);
	}

	public prepareForUpdate(templateProject: TemplateProject, templateTasks: TemplateProjectTask[]): void {
		console.log(`prepareForUpdate | Task: ${this.templateTaskName}, Id: ${this.id}`);

		this.calculateDates(templateProject);

		if (this.newIdToReplaceAutogeneratedGUID > 0) {
			this.id = this.newIdToReplaceAutogeneratedGUID;
		}

		let taskParentIndex: number = templateTasks.findIndex((innerTask) => innerTask.id === this.parentTemplateTaskId);

		if (taskParentIndex >= 0 && templateTasks[taskParentIndex].newIdToReplaceAutogeneratedGUID > 0) {
			this.parentTemplateTaskId = templateTasks[taskParentIndex].newIdToReplaceAutogeneratedGUID;
		}
	}
}

// </Project Templates Specific>
